import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const CHANGE_SEL = "[role=option],[role=checkbox],[role=switch],[role=radio],button[type=button]:not([role=combobox])"
// Navigation-only buttons (tabs, month arrows, expanders) don't count as filling the form
const NAV_SEL = "[data-no-dirty],[role=tab],[data-testid^=assign-mode-],[data-testid^=workout-type-],[data-testid=month-prev],[data-testid=month-next],[data-testid^=month-day-open-],[data-testid=open-lib-panel],[data-testid^=lib-group-]"
const isDismissButton = (btn) => !!btn && (btn.hasAttribute("data-dialog-close") || btn.textContent.trim() === "Cancelar")

// Modals close only via X / Cancelar / action buttons; asks before discarding filled-in fields
const DialogContent = React.forwardRef(({ className, children, onInteractOutside, onEscapeKeyDown, onOpenAutoFocus, ...props }, ref) => {
  const dirty = React.useRef(false)
  const bypass = React.useRef(false)
  const [pending, setPending] = React.useState(null)

  const markDirty = (e) => {
    if (e.target.closest?.("[data-discard-confirm]")) return
    dirty.current = true
  }
  const onClickCapture = (e) => {
    if (e.target.closest?.("[data-discard-confirm]")) return
    const btn = e.target.closest?.("button")
    if (isDismissButton(btn)) {
      if (dirty.current && !bypass.current) {
        e.preventDefault()
        e.stopPropagation()
        setPending(btn)
      }
      return
    }
    if (e.target.closest?.(NAV_SEL)) return
    if (e.target.closest?.(CHANGE_SEL)) dirty.current = true
  }
  // Radix Select picks on pointerup/keydown (no click reaches the option)
  const onOptionPick = (e) => {
    if (e.type === "keydown" && !["Enter", " "].includes(e.key)) return
    if (e.target.closest?.("[role=option]")) dirty.current = true
  }
  const discard = () => {
    const btn = pending
    setPending(null)
    bypass.current = true
    btn?.click()
    bypass.current = false
  }

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onInteractOutside={(e) => { e.preventDefault(); onInteractOutside?.(e); }}
      onEscapeKeyDown={(e) => { e.preventDefault(); onEscapeKeyDown?.(e); }}
      onOpenAutoFocus={(e) => { dirty.current = false; bypass.current = false; setPending(null); onOpenAutoFocus?.(e); }}
      onInputCapture={markDirty}
      onChangeCapture={markDirty}
      onClickCapture={onClickCapture}
      onPointerUpCapture={onOptionPick}
      onKeyDownCapture={onOptionPick}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        data-dialog-close=""
        data-testid="dialog-close-x"
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null) }}>
        <AlertDialogContent data-discard-confirm="" data-testid="discard-confirm" className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl w-[90vw] max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Descartar alterações?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Os dados preenchidos neste formulário serão perdidos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="discard-continue" className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] hover:text-slate-100 rounded-xl mt-0">Continuar editando</AlertDialogCancel>
            <AlertDialogAction data-testid="discard-confirm-yes" onClick={discard} className="bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl">Sim, descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
